import { createFileRoute, Link, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Building2, UserPlus } from "lucide-react"
import { useActiveOrg } from "@/core/active-event"
import { useUserOrgs } from "@/core/orgs"
import { orgMembersQueryOptions } from "@/core/admin"
import { Card, Field, SectionPage } from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/settings/")({
  component: OrgOverview,
})

function OrgOverview() {
  const params = useParams({ from: "/$orgSlug/settings/" })
  const { organizationId } = useActiveOrg()
  const org = useUserOrgs((s) => s.orgs.find((o) => o.slug === params.orgSlug))

  const membersQ = useQuery(orgMembersQueryOptions(organizationId))
  const memberCount = membersQ.data?.members.length ?? 0
  const pendingCount = membersQ.data?.invitations.length ?? 0

  return (
    <SectionPage
      title="Overview"
      description="Your organization on Attend — the tenant every event and member belongs to."
    >
      <Card title="Organization">
        <div className="flex items-center gap-4">
          {org?.logo ? (
            <img
              src={org.logo}
              alt=""
              className="h-14 w-14 shrink-0 rounded-2xl object-cover ring-1 ring-plum-200/50 dark:ring-white/10"
            />
          ) : (
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-plum-100 text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45">
              <Building2 className="h-6 w-6" />
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
              {org?.name ?? params.orgSlug}
            </p>
            <p className="truncate font-mono text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
              {params.orgSlug}
            </p>
          </div>
        </div>
        <Field label="Slug" hint="Used in URLs for this organization's events and settings.">
          <p className="font-mono text-[13px] text-plum-800 dark:text-plum-100">{params.orgSlug}</p>
        </Field>
      </Card>

      <Card
        title="Members"
        description="People with access to this organization's events and settings."
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-[13px] text-plum-800 dark:text-plum-100">
            {/* isPending — gated on the async `organizationId`, so isLoading is
                false while the org resolves and this read "0 members". */}
            {membersQ.isPending ? (
              "Loading…"
            ) : (
              <>
                <span className="font-semibold">{memberCount}</span>{" "}
                {memberCount === 1 ? "member" : "members"}
                {pendingCount > 0 && (
                  <span className="text-plum-700/55 dark:text-plum-200/40">
                    {" "}
                    · {pendingCount} pending
                  </span>
                )}
              </>
            )}
          </p>
          <Link
            to="/$orgSlug/people"
            params={params}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-plum-200/70 bg-white px-3.5 text-[13px] font-semibold text-plum-800 transition hover:bg-plum-100/60 dark:border-white/10 dark:bg-white/5 dark:text-plum-100 dark:hover:bg-white/10"
          >
            <UserPlus className="h-4 w-4" />
            Manage members
          </Link>
        </div>
      </Card>
    </SectionPage>
  )
}
